import React, { useState, useContext } from "react";
import AuthContext from "../context/AuthContext";
import LocationAutocomplete from "./LocationAutocomplete";
import "./Sidebar.css";

const Sidebar = ({ onGenerate, isLoading, onClear }) => {
    const { auth } = useContext(AuthContext);

    const INITIAL_START_LOCATION = "Warszawa, Poland";
    const INITIAL_DESTINATION = "Berlin, Germany";
    const INITIAL_DURATION = "3";
    const INITIAL_BUDGET = "medium";
    const INITIAL_INTENSITY = "medium";
    const INITIAL_TRANSPORT = "car";
    const INITIAL_ACCOMMODATION = "hotel";
    const INITIAL_SEASON = "spring";
    const INITIAL_INTERESTS = [];
    const INITIAL_SIGHTSEEING = false;
    const INITIAL_ROUND_TRIP = false;
    const INITIAL_STOPS = [];
    const INITIAL_TRAVELERS_COUNT = 1;
    const INITIAL_GROUP_TYPE = "solo";

    const [startLocation, setStartLocation] = useState(INITIAL_START_LOCATION);
    const [destination, setDestination] = useState(INITIAL_DESTINATION);
    const [stops, setStops] = useState(INITIAL_STOPS)
    const [duration, setDuration] = useState(INITIAL_DURATION);
    const [budget, setBudget] = useState(INITIAL_BUDGET);
    const [intensity, setIntensity] = useState(INITIAL_INTENSITY);
    const [transport, setTransport] = useState(INITIAL_TRANSPORT);
    const [accommodation, setAccomodation] = useState(INITIAL_ACCOMMODATION);
    const [season, setSeason] = useState(INITIAL_SEASON);
    const [selectedInterests, setSelectedIinterests] = useState(INITIAL_INTERESTS);
    const [sightseeing, setSightseeing] = useState(INITIAL_SIGHTSEEING);
    const [isRoundTrip, setIsRoundTrip] = useState(INITIAL_ROUND_TRIP);
    const [travelersCount, setTravelersCount] = useState(INITIAL_TRAVELERS_COUNT);
    const [groupType, setGroupType] = useState(INITIAL_GROUP_TYPE);

    const PLAN_COST = 50;
    const hasEnoughPoints = auth.user?.points >= PLAN_COST;

    const interestOptions = [
        "History",
        "Art",
        "Nature",
        "Cousine",
        "Sport",
        "Relaxation",
        "Architecture",
        "Music"
    ];

    const toggleInterest = (interest) => {
        setSelectedIinterests((prev) =>
            prev.includes(interest)
                ? prev.filter((i) => i !== interest)
                : [...prev, interest]
        );
    };

    const handleAddStop = () => {
        if (stops.length < 3) {
            setStops([...stops, ""]);
        }
    };

    const handleRemoveStop = (index) => {
        const newStops = stops.filter((_, i) => i !== index);
        setStops(newStops);
    };

    const handleStopChange = (index, value) => {
        const newStops = [...stops];
        newStops[index] = value;
        setStops(newStops);
    };



    const handleGenerateClick = () => {
        if (!startLocation || !destination) {
            alert("Please provide both start location and destination");
            return;
        }

        const selections = {
            startLocation,
            destination,
            stops: stops.filter(s => s.trim() !== ""),
            isRoundTrip,
            duration,
            budget,
            intensity,
            transport,
            accommodation,
            season,
            sightseeing,
            interests: selectedInterests,
            travelersCount: parseInt(travelersCount, 10),
            groupType
        };

        onGenerate(selections);
    };


    const handleClearClick = () => {
        setStartLocation(INITIAL_START_LOCATION);
        setDestination(INITIAL_DESTINATION);
        setStops(INITIAL_STOPS);
        setDuration(INITIAL_DURATION);
        setBudget(INITIAL_BUDGET);
        setIntensity(INITIAL_INTENSITY);
        setTransport(INITIAL_TRANSPORT);
        setAccomodation(INITIAL_ACCOMMODATION);
        setSeason(INITIAL_SEASON);
        setSelectedIinterests(INITIAL_INTERESTS);
        setSightseeing(INITIAL_SIGHTSEEING);
        setIsRoundTrip(INITIAL_ROUND_TRIP);
        setTravelersCount(INITIAL_TRAVELERS_COUNT);
        setGroupType(INITIAL_GROUP_TYPE);

        if (onClear) {
            onClear();
        }
    };

    return (
        <div className="sidebar">
            <div className="sidebar-header">
                <h2>Travel Planner</h2>
                <p>Your ideal trip configuration</p>
            </div>

            <div className="sidebar-section">
                <h3>Route</h3>
                <div className="input-group full-width">
                    <label>Start Location</label>
                    <LocationAutocomplete
                        value={startLocation}
                        onChange={setStartLocation}
                        placeholder="e.g. Warsaw"
                    />
                </div>

                {stops.map((stop, index) => (
                    <div className="input-group full-width" key={index}>
                        <label>Stop nr.{index + 1}</label>
                        <div className="stop-input-wrapper">
                            <LocationAutocomplete
                                value={stop}
                                onChange={(newValue) => handleStopChange(index, newValue)}
                                placeholder="e.g. Poznan"
                            />
                            <button
                                className="remove-stop-btn"
                                onClick={() => handleRemoveStop(index)}
                                title="Remove stop"
                            >
                                x
                            </button>
                        </div>
                    </div>
                ))}

                {stops.length < 3 && (
                    <button className="add-stop-btn" onClick={handleAddStop}>
                        + Add Stop
                    </button>
                )}

                <div className="input-group full-width">
                    <label>Destination</label>
                    <LocationAutocomplete
                        value={destination}
                        onChange={setDestination}
                    />
                </div>

                <div className="input-group">
                    <label>Round trip?</label>
                    <div className="toggle-container">
                        <div
                            className={`switch ${isRoundTrip ? 'checked' : ''}`}
                            onClick={() => setIsRoundTrip(!isRoundTrip)}
                        >
                            <div className="slider"></div>
                        </div>
                        <span className="toggle-label">
                            {isRoundTrip ? "Yes" : "No"}
                        </span>
                    </div>
                </div>
            </div>

            <div className="sidebar-section">
                <h3>Parameters</h3>
                <div className="input-group">
                    <label>Duration (days)</label>
                    <input
                        type="number"
                        min="1"
                        max="14"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                    />
                </div>

                <div className="input-group">
                    <label>Number of travelers</label>
                    <input
                        type="number"
                        min="1"
                        max="10"
                        value={travelersCount}
                        onChange={(e) => setTravelersCount(e.target.value)}
                    />
                </div>

                <div className="input-group">
                    <label>Group Character</label>
                    <select
                        value={groupType}
                        onChange={(e) => setGroupType(e.target.value)}
                    >
                        <option value="solo">Solo</option>
                        <option value="couple">Couple</option>
                        <option value="family">Family (kid friendly)</option>
                        <option value="friends">Friends</option>
                        <option value="business">Business trip</option>
                    </select>
                </div>

                <div className="input-group">
                    <label>Intensity</label>
                    <select
                        value={intensity}
                        onChange={(e) => setIntensity(e.target.value)}
                    >
                        <option value="relaxed">Relaxed</option>
                        <option value="medium">Medium</option>
                        <option value="intense">Intense</option>
                    </select>
                </div>

                <div className="input-group">
                    <label>Budget</label>
                    <select
                        value={budget}
                        onChange={(e) => setBudget(e.target.value)}
                    >
                        <option value="low">Low</option>
                        <option value="medium">Standard</option>
                        <option value="high">Premium</option>
                    </select>
                </div>

                <div className="input-group">
                    <label>Season</label>
                    <select
                        value={season}
                        onChange={(e) => setSeason(e.target.value)}
                    >
                        <option value="spring">Spring</option>
                        <option value="summer">Summer</option>
                        <option value="autumn">Autumn</option>
                        <option value="winter">Winter</option>
                    </select>
                </div>


                <div className="input-group">
                    <label>Transport</label>
                    <select
                        value={transport}
                        onChange={(e) => setTransport(e.target.value)}
                    >
                        <option value="car">Car</option>
                        <option value="airplane">Airplane</option>
                        <option value="train">Train</option>
                        <option value="bicycle">Bike</option>
                        <option value="on-foot">On foot</option>
                    </select>
                </div>

                <div className="input-group">
                    <label>Accommodation</label>
                    <select
                        value={accommodation}
                        onChange={(e) => setAccomodation(e.target.value)}
                    >
                        <option value="hotel">Hotel</option>
                        <option value="apartment">Apartment</option>
                        <option value="hostel">Hostel</option>
                        <option value="camping">Camping</option>
                        <option value="wild">In the wild</option>
                    </select>
                </div>

                <div className="input-group">
                    <label>Sightseeing along the way?</label>
                    <div className="toggle-container">
                        <div
                            className={`switch ${sightseeing ? 'checked' : ''}`}
                            onClick={() => setSightseeing(!sightseeing)}
                        >
                            <div className="slider"></div>
                        </div>
                        <span className="toggle-label">
                            {sightseeing ? 'Yes' : 'No'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="sidebar-section">
                <h3>Your Interests</h3>
                <div className="interest-grid">
                    {interestOptions.map((interest) => (
                        <div
                            key={interest}
                            className={`interest-item ${selectedInterests.includes(interest) ? "selected" : ""
                                }`}
                            onClick={() => toggleInterest(interest)}
                        >
                            {interest}
                        </div>
                    ))}
                </div>
            </div>
            <div className="sidebar-btn-section">
                <button
                    className="generate-btn"
                    onClick={handleGenerateClick}
                    disabled={isLoading || !hasEnoughPoints}
                >
                    {isLoading ? "Creating plan..." : `Generate Plan (${PLAN_COST} pts)`}
                </button>
            </div>
            <div className="sidebar-btn-section">
                <button
                    className="clear-btn"
                    onClick={handleClearClick}
                    disabled={isLoading}
                >
                    Clear All
                </button>
            </div>
            {!hasEnoughPoints && (
                <p className="points-error-msg">Not enough points to generate a plan</p>
            )}
        </div>
    );
};

export default Sidebar;
